import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  max-height: ${props => props.fullHeight ? '90vh' : '70vh'};
  overflow-y: auto;
  padding: 1rem;

  /* Custom Scrollbar - Theme-aware */
  &::-webkit-scrollbar {
    width: 12px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.scrollbarTrack};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.scrollbarThumb};
    border-radius: 6px;
    border: 2px solid ${props => props.theme.scrollbarTrack};
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${props => props.theme.scrollbarThumbHover};
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.scrollbarThumb} ${props => props.theme.scrollbarTrack};
`;

const Title = styled.h2`
  color: ${props => props.theme.text};
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  text-align: center;
  border-bottom: 1px solid ${props => props.theme.border};
  padding-bottom: 1rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.buttonPrimary};
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
`;

const Paragraph = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.9375rem;
  line-height: 1.6;
  margin: 0 0 1rem 0;
`;

const List = styled.ul`
  color: ${props => props.theme.textSecondary};
  font-size: 0.9375rem;
  line-height: 1.6;
  margin: 0 0 1rem 0;
  padding-left: 1.5rem;
`;

const ListItem = styled.li`
  margin-bottom: 0.5rem;
`;

const LastUpdated = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: 0.8125rem;
  text-align: center;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid ${props => props.theme.border};
`;

const TermsOfService = ({ fullHeight = false }) => {
  return (
    <Container fullHeight={fullHeight}>
      <Title>Terms of Service</Title>

      <Section>
        <SectionTitle>1. Acceptance of Terms</SectionTitle>
        <Paragraph>
          By accessing or using PlanMySemester ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>2. Description of Service</SectionTitle>
        <Paragraph>
          PlanMySemester provides academic planning tools, including course management, assignment tracking, GPA calculation, and study assistance. We reserve the right to modify, suspend, or discontinue any part of the Service at any time.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>3. User Accounts</SectionTitle>
        <List>
          <ListItem>You must provide accurate and complete information when creating an account.</ListItem>
          <ListItem>You are responsible for maintaining the security of your account credentials.</ListItem>
          <ListItem>We offer Two-Factor Authentication (2FA) for enhanced security, which we strongly recommend enabling.</ListItem>
          <ListItem>You are responsible for all activities that occur under your account.</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>4. Acceptable Use Policy</SectionTitle>
        <Paragraph>You agree not to:</Paragraph>
        <List>
          <ListItem>Use the Service for any illegal purpose or in violation of any local, state, national, or international law.</ListItem>
          <ListItem>Harass, threaten, or defraud other users.</ListItem>
          <ListItem>Interfere with or damage the Service, including through the use of viruses, cancel bots, Trojan horses, harmful code, flood pings, denial-of-service attacks, packet or IP spoofing, forged routing or electronic mail address information, or similar methods or technology.</ListItem>
          <ListItem>Attempt to reverse engineer or jeopardize the correct functioning of the Service, or otherwise attempt to derive the source code of the software (including the tools, methods, processes, and infrastructure) that enables or underlies the Service.</ListItem>
          <ListItem>Use our tools to generate content that is harmful, hateful, sexually explicit, or promotes violence.</ListItem>
          <ListItem>Share your account credentials with others.</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>5. Subscriptions and Payments</SectionTitle>
        <Paragraph>
          Certain features of the Service may require a paid subscription. By subscribing, you agree to pay the applicable fees and taxes.
        </Paragraph>
        <List>
          <ListItem>Subscriptions automatically renew unless cancelled at least 48 hours before the end of the current period.</ListItem>
          <ListItem>We use third-party payment processors (e.g., Stripe) to handle financial transactions. We do not store your full credit card information.</ListItem>
          <ListItem>Refunds are handled in accordance with our Refund Policy, available upon request or as required by applicable law.</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>6. Study Tools Disclaimer</SectionTitle>
        <Paragraph>
          Our Service includes study assistance tools (e.g., Tutor, Study Guides).
        </Paragraph>
        <List>
          <ListItem>Responses are generated and may not always be accurate, complete, or up-to-date.</ListItem>
          <ListItem>You should verify any information provided, especially for critical academic work.</ListItem>
          <ListItem>We are not responsible for any errors or omissions in the content generated by our tools.</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>7. Intellectual Property</SectionTitle>
        <Paragraph>
          The Service and its original content, features, and functionality are and will remain the exclusive property of PlanMySemester and its licensors. The Service is protected by copyright, trademark, and other laws.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>8. Termination</SectionTitle>
        <Paragraph>
          We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms. Upon termination, your right to use the Service will immediately cease.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>9. Limitation of Liability</SectionTitle>
        <Paragraph>
          In no event shall PlanMySemester, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the Service.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>10. Changes to Terms</SectionTitle>
        <Paragraph>
          We reserve the right, at our sole discretion, to modify or replace these Terms at any time. By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>11. Contact Us</SectionTitle>
        <Paragraph>
          If you have any questions about these Terms, please contact us through the support feature available in your account dashboard.
        </Paragraph>
      </Section>

      <LastUpdated>
        Last Updated: January 1, 2026
      </LastUpdated>
    </Container>
  );
};

export default TermsOfService;
